import React, { useState, useEffect } from 'react';
import { Palette } from 'lucide-react';

interface ColorMatchGameProps {
  onComplete: (score: number) => void;
}

const COLORS = [
    { name: 'אדום', hex: '#ef4444' },
    { name: 'כחול', hex: '#3b82f6' },
    { name: 'ירוק', hex: '#22c55e' },
    { name: 'צהוב', hex: '#eab308' },
    { name: 'סגול', hex: '#a855f7' },
    { name: 'כתום', hex: '#f97316' },
];

export default function ColorMatchGame({ onComplete }: ColorMatchGameProps) {
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [currentWord, setCurrentWord] = useState('');
  const [currentColor, setCurrentColor] = useState('');
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!isActive) return;

    const timer = setInterval(() => {
        setTimeLeft(t => Math.max(0, t - 1));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isActive]);

  useEffect(() => {
    if (timeLeft === 0 && isActive) {
        setIsActive(false);
        onComplete(score);
    }
  }, [timeLeft, isActive, onComplete, score]);

  const nextRound = () => {
    const wordColor = COLORS[Math.floor(Math.random() * COLORS.length)];
    const inkColor = COLORS[Math.floor(Math.random() * COLORS.length)];
    
    setCurrentWord(wordColor.name);
    setCurrentColor(inkColor.hex);
  };

  const handleAnswer = (isMatch: boolean) => {
      // Find object for current word
      const wordObj = COLORS.find(c => c.name === currentWord);
      // Determine if it was actually a match (does the ink color match the word meaning?)
      // Wait, Stroop is usually "Name the color of the text".
      // Let's implement a simpler version: "Does the text match the color?" Yes/No
      
      const actuallyMatch = wordObj?.hex === currentColor;
      
      if (isMatch === actuallyMatch) {
          setScore(s => s + 5);
      } else {
          setScore(s => Math.max(0, s - 2));
      }
      nextRound();
  };
  
  const startGame = () => {
      setScore(0);
      setTimeLeft(30);
      setIsActive(true);
      nextRound();
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-4 text-foreground">
        <h2 className="text-2xl font-bold mb-8 flex items-center gap-2">
            <Palette className="text-purple-400" /> התאמת צבעים
        </h2>

        {!isActive ? (
             <div className="text-center">
                <p className="text-muted-foreground mb-8 max-w-xs mx-auto">
                    האם הצבע של המילה תואם למשמעות שלה?
                    <br/>
                    לדוגמה: המילה "אדום" כתובה בצבע אדום = כן.
                </p>
                <button 
                  onClick={startGame}
                  className="bg-purple-600 hover:bg-purple-500 text-white px-12 py-4 rounded-full font-bold text-xl shadow-lg transition-transform hover:scale-105"
              >
                  התחל משחק
              </button>
            </div>
        ) : (
            <>
                <div className="flex justify-between w-full max-w-xs mb-8 text-xl font-mono">
                    <span>זמן: {timeLeft}</span>
                    <span>ניקוד: {score}</span>
                </div>

                <div 
                    className="text-6xl font-bold mb-16 transition-all duration-300 transform hover:scale-110"
                    style={{ color: currentColor }}
                >
                    {currentWord}
                </div>

                <div className="flex gap-4 w-full max-w-xs">
                    <button 
                        onClick={() => handleAnswer(false)}
                        className="flex-1 py-6 bg-red-600 rounded-xl text-2xl font-bold hover:bg-red-500 transition-colors"
                    >
                        לא
                    </button>
                    <button 
                        onClick={() => handleAnswer(true)}
                        className="flex-1 py-6 bg-green-600 rounded-xl text-2xl font-bold hover:bg-green-500 transition-colors"
                    >
                        כן
                    </button>
                </div>
            </>
        )}
    </div>
  );
}
